import { push, ref, set, serverTimestamp } from "firebase/database";
import { db } from "../lib/firebase";
import { CONFIG } from "../config";

const KEY_FALLBACK_PREFIX = "field";
const WEB3FORMS_ENDPOINT = "https://api.web3forms.com/submit";
const normalizeEmail = (value) => {
  const candidate = String(value || "").trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(candidate) ? candidate : "";
};
const DEFAULT_WEB3FORMS_ACCESS_KEY = String(CONFIG.WEB3FORMS_ACCESS_KEY || "").trim();
const DEFAULT_FORM_NOTIFICATION_EMAIL = normalizeEmail(
  CONFIG.FORM_NOTIFICATION_EMAIL
);

export const normalizeSubmissionKey = (value, fallback = KEY_FALLBACK_PREFIX) => {
  const cleaned = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "");

  return cleaned || fallback;
};

export const getFieldSubmissionKey = (field, index = 0) => {
  const fallback = `${KEY_FALLBACK_PREFIX}_${index + 1}`;
  if (field?.key) return normalizeSubmissionKey(field.key, fallback);
  if (field?.name) return normalizeSubmissionKey(field.name, fallback);
  return fallback;
};

export const buildSubmissionFieldsFromFormData = (formData, fields = []) => {
  if (typeof FormData === "undefined" || !(formData instanceof FormData)) return {};

  const normalizedFields = Array.isArray(fields) ? fields : [];
  if (!normalizedFields.length) {
    return Array.from(formData.entries()).reduce((acc, [rawKey, rawValue]) => {
      if (!rawKey || rawKey === "access_key") return acc;
      const key = normalizeSubmissionKey(rawKey, rawKey);
      acc[key] = String(rawValue ?? "").trim();
      return acc;
    }, {});
  }

  return normalizedFields.reduce((acc, field, index) => {
    const key = getFieldSubmissionKey(field, index);
    const value = formData.get(key);
    acc[key] = String(value ?? "").trim();
    return acc;
  }, {});
};

export async function saveFormSubmission({
  formId = "",
  formName = "",
  formType = "form",
  fields = {},
  metadata = {},
  sourcePath = typeof window !== "undefined" ? window.location.pathname : "",
} = {}) {
  const submissionsRef = ref(db, "formSubmissions");
  const submissionRef = push(submissionsRef);
  const now = new Date();

  const normalizedFields = Object.entries(fields || {}).reduce((acc, [k, v]) => {
    if (!k) return acc;
    const key = normalizeSubmissionKey(k, k);
    acc[key] = String(v ?? "").trim();
    return acc;
  }, {});

  const payload = {
    formId: String(formId || ""),
    formName: String(formName || ""),
    formType: String(formType || "form"),
    sourcePath: String(sourcePath || ""),
    fields: normalizedFields,
    metadata: metadata || {},
    createdAt: serverTimestamp(),
    createdAtClient: now.getTime(),
    submittedAtIso: now.toISOString(),
  };

  await set(submissionRef, payload);
  return submissionRef.key;
}

export async function notifySubmissionByEmail({
  accessKey = "",
  formName = "Website Form",
  fields = {},
  metadata = {},
  toEmail = "",
  sourcePath = typeof window !== "undefined" ? window.location.pathname : "",
} = {}) {
  const trimmedAccessKey = String(accessKey || "").trim() || DEFAULT_WEB3FORMS_ACCESS_KEY;
  if (!trimmedAccessKey) {
    return { notified: false, reason: "missing-access-key" };
  }

  const safeToEmail = normalizeEmail(toEmail) || DEFAULT_FORM_NOTIFICATION_EMAIL;
  const payload = new FormData();
  payload.append("access_key", trimmedAccessKey);
  payload.append("subject", `${String(formName || "Website Form").trim()} submission`);
  payload.append("source_path", String(sourcePath || "").trim());
  if (safeToEmail) payload.append("to_email", safeToEmail);

  Object.entries(fields || {}).forEach(([rawKey, rawValue]) => {
    const key = normalizeSubmissionKey(rawKey, "");
    if (!key) return;
    payload.append(key, String(rawValue ?? "").trim());
  });

  Object.entries(metadata || {}).forEach(([rawKey, rawValue]) => {
    const key = normalizeSubmissionKey(rawKey, "");
    if (!key) return;
    payload.append(`meta_${key}`, String(rawValue ?? "").trim());
  });

  const response = await fetch(WEB3FORMS_ENDPOINT, {
    method: "POST",
    body: payload,
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok || data?.success === false) {
    const msg =
      String(data?.message || "").trim() ||
      `Email notification failed (${response.status})`;
    throw new Error(msg);
  }

  return { notified: true };
}
